import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { DIET_OPTIONS, ALLERGY_OPTIONS, MEAL_TYPE_OPTIONS, MAX_TIME_OPTIONS } from '../constants/profile';
import { DIFFICULTY_OPTIONS } from '../constants/recipe';
import Icon from './Icon';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/recipes';

const ALL_MEAL_TYPES = MEAL_TYPE_OPTIONS.map((m) => m.value);

interface Profile {
  diet_preference: string;
  allergies: string[];
  planned_meal_types: string[];
  difficulty_levels: string[];
  max_total_time: number | null;
}

interface Props {
  onMessage: (message: { type: 'success' | 'error'; text: string }) => void;
}

const sameSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

// Lifestyle (diet) and Allergies/Restrictions both edit the same UserProfile
// record but save independently -- so picking a diet doesn't force you to
// also commit in-progress allergy edits, and vice versa.
const ProfileSettings: React.FC<Props> = ({ onMessage }) => {
  const queryClient = useQueryClient();
  const [diet, setDiet] = useState('none');
  const [allergies, setAllergies] = useState<string[]>([]);
  const [customAllergy, setCustomAllergy] = useState('');
  const [plannedMeals, setPlannedMeals] = useState<string[]>(ALL_MEAL_TYPES);
  const [difficulties, setDifficulties] = useState<string[]>(DIFFICULTY_OPTIONS);
  const [maxTime, setMaxTime] = useState<number | null>(null);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/profile/`);
      return response.data as Profile;
    },
  });

  useEffect(() => {
    if (profile) {
      setDiet(profile.diet_preference);
      setAllergies(profile.allergies || []);
      setPlannedMeals(profile.planned_meal_types?.length ? profile.planned_meal_types : ALL_MEAL_TYPES);
      setDifficulties(profile.difficulty_levels?.length ? profile.difficulty_levels : DIFFICULTY_OPTIONS);
      setMaxTime(profile.max_total_time ?? null);
    }
  }, [profile]);

  const errorDetail = (error: unknown): string | null =>
    axios.isAxiosError(error) ? error.response?.data?.error ?? null : null;

  const saveDietMutation = useMutation({
    mutationFn: async (diet_preference: string) => {
      await axios.put(`${API_URL}/profile/update/`, { diet_preference });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      onMessage({ type: 'success', text: 'Lifestyle preference saved.' });
    },
    onError: (error) => {
      onMessage({ type: 'error', text: errorDetail(error) || 'Error saving lifestyle preference.' });
    },
  });

  const saveAllergiesMutation = useMutation({
    mutationFn: async (nextAllergies: string[]) => {
      await axios.put(`${API_URL}/profile/update/`, { allergies: nextAllergies });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      onMessage({ type: 'success', text: 'Allergies & restrictions saved.' });
    },
    onError: (error) => {
      onMessage({ type: 'error', text: errorDetail(error) || 'Error saving allergies & restrictions.' });
    },
  });

  const savePlanningMutation = useMutation({
    mutationFn: async (planned_meal_types: string[]) => {
      await axios.put(`${API_URL}/profile/update/`, { planned_meal_types });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      onMessage({ type: 'success', text: 'Planning preferences saved.' });
    },
    onError: (error) => {
      onMessage({ type: 'error', text: errorDetail(error) || 'Error saving planning preferences.' });
    },
  });

  const saveCookingEffortMutation = useMutation({
    mutationFn: async (values: { difficulty_levels: string[]; max_total_time: number | null }) => {
      await axios.put(`${API_URL}/profile/update/`, values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      onMessage({ type: 'success', text: 'Time & difficulty saved.' });
    },
    onError: (error) => {
      onMessage({ type: 'error', text: errorDetail(error) || 'Error saving time & difficulty.' });
    },
  });

  const toggleDifficulty = (value: string) => {
    setDifficulties((prev) => (prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]));
  };

  const toggleMeal = (value: string) => {
    setPlannedMeals((prev) => (prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]));
  };

  const toggleAllergy = (name: string) => {
    setAllergies((prev) => (prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name]));
  };

  const addCustomAllergy = () => {
    const trimmed = customAllergy.trim();
    if (trimmed && !allergies.includes(trimmed)) {
      setAllergies((prev) => [...prev, trimmed]);
    }
    setCustomAllergy('');
  };

  const dietDirty = profile ? diet !== profile.diet_preference : false;
  const allergiesDirty = profile ? !sameSet(allergies, profile.allergies || []) : false;
  const plannedMealsDirty = profile
    ? !sameSet(plannedMeals, profile.planned_meal_types?.length ? profile.planned_meal_types : ALL_MEAL_TYPES)
    : false;
  const cookingEffortDirty = profile
    ? !sameSet(difficulties, profile.difficulty_levels?.length ? profile.difficulty_levels : DIFFICULTY_OPTIONS)
      || maxTime !== (profile.max_total_time ?? null)
    : false;

  return (
    <>
      <div className="settings-card">
        <Icon name="leaf" className="icon card-icon" />
        <h2>Lifestyle</h2>
        <p>Choose a diet to guide the recipes RecipeEngine recommends.</p>

        <div className="option-pills">
          {DIET_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`option-pill ${diet === opt.value ? 'active' : ''}`}
              onClick={() => setDiet(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => saveDietMutation.mutate(diet)}
          disabled={!dietDirty || saveDietMutation.isPending}
          className="btn-primary settings-card-save"
        >
          {saveDietMutation.isPending ? 'Saving...' : 'Save Lifestyle'}
        </button>
      </div>

      <div className="settings-card">
        <Icon name="calendar" className="icon card-icon" />
        <h2>Planning</h2>
        <p>Choose which meals to plan for. Fewer meals means faster, cheaper plan generation.</p>

        <div className="option-pills">
          <button
            type="button"
            className={`option-pill ${plannedMeals.length === ALL_MEAL_TYPES.length ? 'active' : ''}`}
            onClick={() => setPlannedMeals(ALL_MEAL_TYPES)}
          >
            All Meals
          </button>
          {MEAL_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`option-pill ${plannedMeals.includes(opt.value) ? 'active' : ''}`}
              onClick={() => toggleMeal(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => savePlanningMutation.mutate(plannedMeals)}
          disabled={!plannedMealsDirty || plannedMeals.length === 0 || savePlanningMutation.isPending}
          className="btn-primary settings-card-save"
        >
          {savePlanningMutation.isPending ? 'Saving...' : 'Save Planning'}
        </button>
      </div>

      <div className="settings-card">
        <Icon name="clock" className="icon card-icon" />
        <h2>Time &amp; Difficulty</h2>
        <p>Only suggest recipes at these skill levels, ready within this much total time (prep + cook).</p>

        <div className="option-pills">
          <button
            type="button"
            className={`option-pill ${difficulties.length === DIFFICULTY_OPTIONS.length ? 'active' : ''}`}
            onClick={() => setDifficulties(DIFFICULTY_OPTIONS)}
          >
            Any Level
          </button>
          {DIFFICULTY_OPTIONS.map((level) => (
            <button
              key={level}
              type="button"
              className={`option-pill ${difficulties.includes(level) ? 'active' : ''}`}
              onClick={() => toggleDifficulty(level)}
            >
              {level}
            </button>
          ))}
        </div>

        <div className="option-pills">
          {MAX_TIME_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              className={`option-pill ${maxTime === opt.value ? 'active' : ''}`}
              onClick={() => setMaxTime(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => saveCookingEffortMutation.mutate({ difficulty_levels: difficulties, max_total_time: maxTime })}
          disabled={!cookingEffortDirty || difficulties.length === 0 || saveCookingEffortMutation.isPending}
          className="btn-primary settings-card-save"
        >
          {saveCookingEffortMutation.isPending ? 'Saving...' : 'Save Time & Difficulty'}
        </button>
      </div>

      <div className="settings-card">
        <Icon name="ban" className="icon card-icon" />
        <h2>Allergies &amp; Restrictions</h2>
        <p>Recipes will avoid these ingredients. Pick common ones or add your own.</p>

        {allergies.length > 0 && (
          <div className="allergy-chips">
            {allergies.map((a) => (
              <span key={a} className="allergy-chip">
                {a}
                <button type="button" onClick={() => toggleAllergy(a)} aria-label={`Remove ${a}`}>✕</button>
              </span>
            ))}
          </div>
        )}

        <div className="option-pills">
          {ALLERGY_OPTIONS.filter((a) => !allergies.includes(a)).map((a) => (
            <button key={a} type="button" className="option-pill" onClick={() => toggleAllergy(a)}>
              + {a}
            </button>
          ))}
        </div>

        <div className="custom-allergy-row">
          <input
            type="text"
            value={customAllergy}
            onChange={(e) => setCustomAllergy(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomAllergy();
              }
            }}
            placeholder="Add another (e.g. Corn Allergy)"
          />
          <button type="button" onClick={addCustomAllergy} className="btn-secondary">Add</button>
        </div>

        <button
          onClick={() => saveAllergiesMutation.mutate(allergies)}
          disabled={!allergiesDirty || saveAllergiesMutation.isPending}
          className="btn-primary settings-card-save"
        >
          {saveAllergiesMutation.isPending ? 'Saving...' : 'Save Restrictions'}
        </button>
      </div>
    </>
  );
};

export default ProfileSettings;
